import { partialDateLabel } from "./partialDate";
import { calendarDateLabel, localDateTimeLabel } from "./caseReviewDateTime";
import type { CaseFieldOption, CaseFieldValue } from "../model/caseReview";

/** Read mode presents labels without converting calendar dates through a timezone. */
export function caseFieldDisplayValue(value: CaseFieldValue, kind: string, options?: CaseFieldOption[]): string {
  if (value === null || value === "") return "Not specified";
  if (kind === "partial-date" && typeof value === "string") return partialDateLabel(value);
  if (kind === "coded") return options?.find(option => String(option.id) === String(value))?.label ?? "Unavailable option";
  if (kind === "boolean") return value === true ? "Yes" : "No";
  // A date-time is an instant; a date remains the same calendar day in every timezone.
  if (kind === "date" && typeof value === "string") return calendarDateLabel(value) ?? value;
  if (kind === "datetime" && typeof value === "string") return localDateTimeLabel(value) ?? value;
  if (kind === "criteria" && typeof value === "object") {
    const selected = options?.filter((option) => value[String(option.id)] === true).map((option) => option.label) ?? [];
    return selected.length ? selected.join(", ") : "None selected";
  }
  return String(value);
}

/**
 * A criteria document as the audit trail stores it, with the ticked criteria's labels: the stored keys are the
 * catalog's storage keys ("Life Threatening") or the older snake_case ones, and the form's keys are snake_case. Text
 * that is not a JSON object is shown as it is.
 */
export function storedCriteriaDisplayValue(text: string, options?: CaseFieldOption[]): string {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return text;
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return text;
  const value = Object.fromEntries(Object.entries(parsed as Record<string, unknown>)
    .map(([key, flag]) => [key.trim().toLowerCase().replace(/\s+/g, "_"), flag === true]));
  return caseFieldDisplayValue(value, "criteria", options);
}
